// components/TicketPopup.jsx
import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";

import { ticketService } from "../../../../services/api/ticketService";
import { AuthContext } from "../../../../contexts/AuthContext";
import Modal from "../../components/Modal";

/** ====== Helpers (hooks) ====== */

// Auto-resize cho <textarea>
function useAutoResizeTextArea(ref, value) {
  useEffect(() => {
    const el = ref?.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${el.scrollHeight}px`;
  }, [value, ref]);
}

/** ====== Component ====== */
const MAX_NOTE = 2000;

export default function TicketPopup({
  isOpen,
  onClose,
  profileID,
  profileName,
}) {
  const navigate = useNavigate();
  const { token_client } = useContext(AuthContext);

  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  const textAreaRef = useRef(null);

  // Auto-resize
  useAutoResizeTextArea(textAreaRef, note);

  const noteErr = useMemo(() => {
    const v = note.trim();
    if (!v) return "Vui lòng nhập lời nhắn.";
    if (v.length > MAX_NOTE) return `Tối đa ${MAX_NOTE} ký tự.`;
    return "";
  }, [note]);

  const canSubmit = !isSubmitting && !noteErr && note.trim().length > 0;

  // Reset state when closing/opening
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        textAreaRef.current?.focus();
      }, 0);
    } else {
      setNote("");
      setTouched(false);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(
    async (e) => {
      if (e) e?.preventDefault(); // Handle both form submit and button click
      setTouched(true);
      if (!canSubmit) return;

      setIsSubmitting(true);
      try {
        await ticketService.create_ticket_by_profileid(
          profileID,
          note.trim(),
          token_client,
        );
        toast.success("Gửi thành công!");
        navigate("/danh-sach-yeu-cau");
      } catch (error) {
        console.error("Lỗi khi tạo ticket: ", error);
        toast.dismiss();
        toast.error("Xảy ra lỗi khi tạo ticket!");
      } finally {
        setIsSubmitting(false);
      }
    },
    [canSubmit, navigate, note, profileID, token_client],
  );

  const closeIfAllowed = () => {
    if (!isSubmitting) onClose?.();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeIfAllowed}
      title="Tạo yêu cầu mới"
      maxWidth="672px" // Matches max-w-2xl

      // Footer Actions via Props
      primaryButtonText="Gửi ngay"
      primaryButtonOnClick={handleSubmit}
      primaryButtonDisabled={!canSubmit}
      isLoading={isSubmitting} // New prop for loading state

      cancelButtonText="Hủy"
    // onClose is already passed, which handles cancel button click by default
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        {/* Tên hồ sơ */}
        <div className="flex flex-col gap-1">
          <label htmlFor="profileName" className="text-sm text-gray-700">
            Tên hồ sơ
          </label>
          <input
            id="profileName"
            name="profileName"
            type="text"
            value={profileName || ""}
            disabled
            readOnly
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-600 outline-none transition focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
          />
        </div>

        {/* Lời nhắn */}
        <div className="flex flex-col gap-1">
          <label htmlFor="note" className="text-sm text-gray-700">
            Lời nhắn
          </label>

          <div className="relative">
            <textarea
              ref={textAreaRef}
              id="note"
              name="note"
              placeholder="Để lại lời nhắn"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => setTouched(true)}
              rows={5}
              maxLength={MAX_NOTE}
              className={`w-full resize-none rounded-lg border px-4 py-3 text-sm outline-none transition focus:ring-2 focus:ring-blue-500 ${touched && noteErr ? "border-red-400" : "border-gray-300"
                } text-[#4B5563]`}
              required
            />
            {/* Counter */}
            <div className="pointer-events-none absolute bottom-2 right-3 text-xs text-gray-400">
              {note.trim().length}/{MAX_NOTE}
            </div>
          </div>

          {/* Error */}
          <AnimatePresence>
            {touched && noteErr && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-xs text-red-500"
              >
                {noteErr}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>
    </Modal>
  );
}
